import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  Inbox as InboxIcon,
  Settings2,
  RefreshCw,
  Mail,
  MailOpen,
  Paperclip,
  ArrowLeft,
  Trash2,
  Send,
  Info,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const IONOS_DEFAULTS = {
  host: "imap.ionos.de",
  port: 993,
  use_ssl: true,
  mailbox: "INBOX",
};

function formatDate(raw) {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    const today = new Date();
    const sameDay =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate();
    if (sameDay) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getFullYear()).slice(-2)}`;
  } catch {
    return raw;
  }
}

function initials(email) {
  const name = (email || "").replace(/<.*>/, "").trim();
  const match = name.match(/^([A-Z])/i);
  return (match ? match[1] : "@").toUpperCase();
}

export default function InboxPage() {
  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadConfig = async () => {
    try {
      const r = await api.get("/inbox/config");
      setConfig(r.data);
      if (!r.data.configured) setShowConfig(true);
      return r.data.configured;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      return false;
    }
  };

  const loadMessages = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/inbox/messages", { params: { limit: 100 } });
      setMessages(r.data || []);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      // Not configured yet — silent
      if (!/nicht konfiguriert/i.test(detail || "")) {
        toast.error(detail);
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      const configured = await loadConfig();
      if (configured) await loadMessages();
      setLoading(false);
    })();
  }, []);

  const openMessage = async (uid) => {
    setSelectedUid(uid);
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await api.get(`/inbox/messages/${encodeURIComponent(uid)}`);
      setDetail(r.data);
      // Mark seen locally
      setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen: true } : m)));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const deleteMessage = async () => {
    if (!selectedUid) return;
    if (!window.confirm("E-Mail unwiderruflich löschen?")) return;
    try {
      await api.delete(`/inbox/messages/${encodeURIComponent(selectedUid)}`);
      toast.success("Gelöscht");
      setDetail(null);
      setSelectedUid(null);
      await loadMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (loading) {
    return <div className="p-8 text-xs text-slate-500">Lädt…</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8" data-testid="inbox-page">
      {/* Header */}
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
            <InboxIcon size={12} strokeWidth={1.5} /> Portal · Sammelpostfach
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-light tracking-tighter text-[#0A0A0A] mt-1">
            Post<span className="text-[#00CCC9]">fach</span>
          </h1>
          {config?.configured && (
            <div className="text-[11px] font-mono text-slate-500 mt-1">
              {config.username} · {config.host}:{config.port}
              {config.use_ssl ? " · SSL" : ""}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadMessages}
            disabled={refreshing || !config?.configured}
            className="border border-slate-300 hover:bg-slate-100 disabled:opacity-40 px-3 py-2 text-xs font-medium flex items-center gap-2"
            data-testid="inbox-refresh-btn"
          >
            <RefreshCw
              size={12}
              strokeWidth={1.5}
              className={refreshing ? "animate-spin" : ""}
            />
            Aktualisieren
          </button>
          <button
            onClick={() => setShowConfig(true)}
            className="bg-[#0A0A0A] text-white hover:bg-[#00CCC9] hover:text-[#0A0A0A] px-3 py-2 text-xs font-medium flex items-center gap-2"
            data-testid="inbox-config-btn"
          >
            <Settings2 size={12} strokeWidth={1.5} /> Einstellungen
          </button>
        </div>
      </div>

      {!config?.configured ? (
        <NotConfigured onSetup={() => setShowConfig(true)} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] border border-slate-200 min-h-[600px] bg-white">
          {/* Message list */}
          <aside
            className={`border-b lg:border-b-0 lg:border-r border-slate-200 ${
              detail ? "hidden lg:block" : "block"
            }`}
            data-testid="inbox-list"
          >
            {messages.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500 italic">
                Noch keine E-Mails im Sammelpostfach.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {messages.map((m) => (
                  <li key={m.uid}>
                    <button
                      onClick={() => openMessage(m.uid)}
                      data-testid={`inbox-item-${m.uid}`}
                      className={`w-full text-left p-3 hover:bg-slate-50 flex items-start gap-3 ${
                        selectedUid === m.uid
                          ? "bg-[#00CCC9]/10 border-l-2 border-l-[#00CCC9]"
                          : ""
                      } ${!m.seen ? "bg-[#FFFBE6]/50" : ""}`}
                    >
                      <div
                        className={`w-8 h-8 flex items-center justify-center text-xs font-bold shrink-0 ${
                          m.seen ? "bg-slate-100 text-slate-600" : "bg-[#00CCC9] text-[#0A0A0A]"
                        }`}
                      >
                        {initials(m.from)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div
                            className={`text-xs truncate ${
                              m.seen ? "text-slate-600" : "font-semibold text-slate-900"
                            }`}
                          >
                            {m.from || "—"}
                          </div>
                          <div className="text-[9px] font-mono text-slate-400 shrink-0">
                            {formatDate(m.date)}
                          </div>
                        </div>
                        <div
                          className={`text-xs mt-0.5 truncate ${
                            m.seen ? "text-slate-700" : "font-semibold text-slate-900"
                          }`}
                        >
                          {m.subject}
                        </div>
                        <div className="text-[10px] font-mono text-slate-400 mt-0.5 truncate">
                          an {m.to || "—"}
                        </div>
                      </div>
                      {!m.seen && (
                        <div className="w-1.5 h-1.5 rounded-full bg-[#FFD100] mt-2 shrink-0" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Message detail */}
          <main
            className={`min-w-0 ${detail || detailLoading ? "block" : "hidden lg:block"}`}
            data-testid="inbox-detail"
          >
            {detailLoading && (
              <div className="p-10 text-center text-xs text-slate-500">Lädt E-Mail…</div>
            )}
            {!detailLoading && !detail && (
              <div className="p-16 text-center">
                <Mail size={40} strokeWidth={1.5} className="mx-auto text-slate-300 mb-3" />
                <div className="text-sm text-slate-500 italic">Wähle eine E-Mail links aus.</div>
              </div>
            )}
            {detail && !detailLoading && (
              <MessageView
                m={detail}
                onBack={() => {
                  setDetail(null);
                  setSelectedUid(null);
                }}
                onDelete={deleteMessage}
              />
            )}
          </main>
        </div>
      )}

      {showConfig && (
        <ConfigDialog
          initial={config?.configured ? config : IONOS_DEFAULTS}
          onClose={() => setShowConfig(false)}
          onSaved={async () => {
            setShowConfig(false);
            await loadConfig();
            await loadMessages();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────── Sub-components ─────────────── */

function NotConfigured({ onSetup }) {
  return (
    <div className="border border-dashed border-slate-300 p-10 bg-white max-w-2xl mx-auto" data-testid="inbox-not-configured">
      <InboxIcon size={32} strokeWidth={1.5} className="text-[#00CCC9] mb-3" />
      <h2 className="font-heading text-2xl font-light tracking-tighter mb-2">
        Sammelpostfach noch nicht verbunden
      </h2>
      <p className="text-sm text-slate-600 mb-4">
        Verbinde dein IONOS-Postfach (z.B. <code className="font-mono">licenses@we-academy.de</code>){" "}
        einmalig, damit du hier alle Software-Bestätigungs-Mails an{" "}
        <code className="font-mono">*@we-academy.de</code>-Aliase siehst.
      </p>
      <div className="bg-slate-50 border-l-2 border-[#00CCC9] p-4 text-xs text-slate-700 space-y-1 mb-4">
        <div className="font-bold uppercase tracking-overline text-[10px] text-slate-500 mb-2 flex items-center gap-1">
          <Info size={11} strokeWidth={1.5} /> Zugangsdaten für IONOS
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono">
          <div className="text-slate-500">IMAP-Server:</div>
          <div>imap.ionos.de</div>
          <div className="text-slate-500">Port:</div>
          <div>993 · SSL</div>
          <div className="text-slate-500">Benutzername:</div>
          <div>vollständige E-Mail-Adresse</div>
          <div className="text-slate-500">Passwort:</div>
          <div>dein Postfach-Passwort</div>
        </div>
      </div>
      <button
        onClick={onSetup}
        data-testid="inbox-setup-btn"
        className="bg-[#00CCC9] text-[#0A0A0A] font-semibold px-4 py-2 text-sm hover:brightness-105"
      >
        Postfach jetzt verbinden
      </button>
    </div>
  );
}

function MessageView({ m, onBack, onDelete }) {
  const safeHtml = m.html
    ? DOMPurify.sanitize(m.html, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ["target"],
      })
    : "";

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-100 px-5 py-4 flex items-start justify-between gap-3">
        <button
          onClick={onBack}
          className="lg:hidden -ml-2 p-2 hover:bg-slate-100"
          data-testid="msg-back-btn"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono text-slate-500">{formatDate(m.date)}</div>
          <h2 className="font-heading text-xl lg:text-2xl font-light tracking-tighter mt-1 text-slate-900">
            {m.subject}
          </h2>
          <div className="text-xs text-slate-600 mt-2 space-y-0.5">
            <div>
              <span className="text-slate-400">Von:</span>{" "}
              <span className="font-medium">{m.from}</span>
            </div>
            <div>
              <span className="text-slate-400">An:</span> <span className="font-mono">{m.to}</span>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-xs border border-red-300 text-red-700 hover:bg-red-600 hover:text-white px-3 py-2 flex items-center gap-1 shrink-0"
          data-testid="msg-delete-btn"
        >
          <Trash2 size={12} strokeWidth={1.5} /> Löschen
        </button>
      </div>
      {(m.attachments || []).length > 0 && (
        <div className="border-b border-slate-100 px-5 py-3 flex flex-wrap gap-2 items-center">
          <span className="text-[10px] font-bold uppercase tracking-overline text-slate-500">
            <Paperclip size={11} strokeWidth={1.5} className="inline mr-1" />
            {m.attachments.length} Anhang
            {m.attachments.length !== 1 ? "e" : ""}:
          </span>
          {m.attachments.map((a, i) => (
            <span
              key={i}
              className="text-[11px] px-2 py-1 border border-slate-200 bg-slate-50 font-mono"
            >
              {a.filename} · {Math.round((a.size || 0) / 1024)} KB
            </span>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-5">
        {safeHtml ? (
          <div
            className="prose prose-sm max-w-none prose-a:text-[#00CCC9]"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">
            {m.text || "(kein Inhalt)"}
          </pre>
        )}
      </div>
    </div>
  );
}

function ConfigDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    host: initial.host || IONOS_DEFAULTS.host,
    port: initial.port || IONOS_DEFAULTS.port,
    use_ssl: initial.use_ssl !== false,
    username: initial.username || "",
    password: "",
    mailbox: initial.mailbox || "INBOX",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const test = async () => {
    if (!form.host || !form.username || !form.password) {
      return toast.error("Bitte Host, Benutzername und Passwort ausfüllen");
    }
    setTesting(true);
    try {
      const r = await api.post("/inbox/config/test", form);
      toast.success(`Verbindung OK · ${r.data.message_count} Nachrichten im Postfach`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!form.host || !form.username || !form.password) {
      return toast.error("Bitte Host, Benutzername und Passwort ausfüllen");
    }
    setSaving(true);
    try {
      await api.post("/inbox/config", form);
      toast.success("Postfach gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="inbox-config-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="text-xs uppercase text-slate-500 font-mono">Portal · IMAP-Verbindung</div>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            Postfach verbinden
          </DialogTitle>
          <DialogDescription className="sr-only">
            IONOS-Zugangsdaten für das Sammelpostfach eingeben.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-[#00CCC9]/10 border-l-2 border-[#00CCC9] p-3 text-[11px] text-slate-700">
            <strong>IONOS-Standardwerte</strong> sind bereits vorausgefüllt. Du musst nur{" "}
            <strong>Benutzername</strong> (deine E-Mail-Adresse) und <strong>Passwort</strong> eintragen.
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="IMAP-Server">
                <input
                  type="text"
                  value={form.host}
                  onChange={(e) => set("host", e.target.value)}
                  className="input-tech font-mono"
                  data-testid="cfg-host"
                />
              </Field>
            </div>
            <Field label="Port">
              <input
                type="number"
                value={form.port}
                onChange={(e) => set("port", parseInt(e.target.value) || 993)}
                className="input-tech font-mono"
                data-testid="cfg-port"
              />
            </Field>
          </div>
          <Field label="Benutzername (volle E-Mail-Adresse)">
            <input
              type="email"
              value={form.username}
              onChange={(e) => set("username", e.target.value)}
              placeholder="lizenzen@beispiel.de"
              className="input-tech font-mono"
              data-testid="cfg-username"
            />
          </Field>
          <Field label="Passwort">
            <input
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder={initial.username ? "leer lassen, um beizubehalten" : "••••••••"}
              className="input-tech font-mono"
              data-testid="cfg-password"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Postfach-Ordner">
              <input
                type="text"
                value={form.mailbox}
                onChange={(e) => set("mailbox", e.target.value)}
                className="input-tech font-mono"
                data-testid="cfg-mailbox"
              />
            </Field>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.use_ssl}
                  onChange={(e) => set("use_ssl", e.target.checked)}
                  className="accent-[#00CCC9]"
                  data-testid="cfg-ssl"
                />
                SSL/TLS verwenden (empfohlen)
              </label>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-between items-center gap-2">
          <button
            onClick={test}
            disabled={testing}
            className="text-xs border border-slate-300 hover:bg-slate-100 px-3 py-2 disabled:opacity-50 flex items-center gap-1"
            data-testid="cfg-test"
          >
            <RefreshCw size={12} strokeWidth={1.5} className={testing ? "animate-spin" : ""} />
            {testing ? "Prüft…" : "Verbindung testen"}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-100">
              Abbrechen
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 text-sm bg-[#00CCC9] text-[#0A0A0A] font-semibold hover:brightness-105 disabled:opacity-50"
              data-testid="cfg-save"
            >
              {saving ? "Speichert…" : "Speichern"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1.5 block">
        {label}
      </label>
      {children}
    </div>
  );
}
